import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';

export type MessagingProvider = 'whatsapp' | 'facebook-messenger' | 'telegram' | 'instagram' | 'x-twitter' | 'phone' | 'unknown';

interface ProviderIndicatorProps {
  provider: MessagingProvider;
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
  style?: any;
}

const providerConfig = {
  'whatsapp': {
    icon: 'logo-whatsapp' as const,
    color: '#25D366',
    label: 'WhatsApp',
    backgroundColor: '#E8F5E8'
  },
  'facebook-messenger': {
    icon: 'logo-facebook' as const,
    color: '#0084FF',
    label: 'Facebook',
    backgroundColor: '#E3F2FD'
  },
  'telegram': {
    icon: 'send' as const,
    color: '#0088CC',
    label: 'Telegram',
    backgroundColor: '#E3F2FD'
  },
  'instagram': {
    icon: 'logo-instagram' as const,
    color: '#E4405F',
    label: 'Instagram',
    backgroundColor: '#FCE4EC'
  },
  'x-twitter': {
    icon: 'logo-twitter' as const,
    color: '#000000',
    label: 'X (Twitter)',
    backgroundColor: '#F3F4F6'
  },
  'phone': {
    icon: 'call' as const,
    color: '#0EA5E9',
    label: 'Phone',
    backgroundColor: '#E0F2FE'
  },
  'unknown': {
    icon: 'chatbubble' as const,
    color: '#6B7280',
    label: 'Unknown',
    backgroundColor: '#F3F4F6'
  }
};

export const ProviderIndicator: React.FC<ProviderIndicatorProps> = ({
  provider,
  size = 'medium',
  showLabel = false,
  style
}) => {
  const config = providerConfig[provider] || providerConfig.unknown;
  
  const sizeConfig = {
    small: {
      iconSize: 12,
      containerSize: 20,
      fontSize: 10
    },
    medium: {
      iconSize: 16,
      containerSize: 28,
      fontSize: 12
    },
    large: {
      iconSize: 20,
      containerSize: 36,
      fontSize: 14
    }
  };

  const currentSize = sizeConfig[size];

  return (
    <View style={[
      styles.container,
      {
        width: currentSize.containerSize,
        height: currentSize.containerSize,
        backgroundColor: config.backgroundColor,
        borderRadius: currentSize.containerSize / 2,
      },
      style
    ]}>
      <Ionicons
        name={config.icon}
        size={currentSize.iconSize}
        color={config.color}
      />
      {showLabel && (
        <Text style={[
          styles.label,
          {
            fontSize: currentSize.fontSize,
            color: config.color,
          }
        ]}>
          {config.label}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  label: {
    fontWeight: '600',
    marginTop: 2,
    marginLeft: spacing.xs,
  },
});

// Helper function to determine provider from message data
export const getProviderFromMessage = (message: any): MessagingProvider => {
  if (message.providerId) {
    if (message.providerId.includes('whatsapp') || message.providerId.includes('wabu')) {
      return 'whatsapp';
    }
    if (message.providerId.includes('facebook') || message.providerId.includes('fbm')) {
      return 'facebook-messenger';
    }
    if (message.providerId.includes('telegram')) {
      return 'telegram';
    }
    if (message.providerId.includes('instagram')) {
      return 'instagram';
    }
  }
  
  // Fallback based on channel
  if (message.channel === 'whatsapp') return 'whatsapp';
  if (message.channel === 'facebook-messenger') return 'facebook-messenger';
  if (message.channel === 'x-twitter') return 'x-twitter';
  if (message.providerId?.startsWith('xt-')) return 'x-twitter';

  return 'unknown';
};

// Helper function to get provider from conversation
export const getProviderFromConversation = (conversation: any): MessagingProvider => {
  if (conversation.channel) {
    if (conversation.channel === 'whatsapp') return 'whatsapp';
    if (conversation.channel === 'facebook-messenger') return 'facebook-messenger';
    if (conversation.channel === 'x-twitter') return 'x-twitter';
    if (conversation.channel === 'phone') return 'phone';
  }

  if (conversation.providerId) {
    if (conversation.providerId.includes('whatsapp') || conversation.providerId.includes('wabu')) return 'whatsapp';
    if (conversation.providerId.includes('facebook') || conversation.providerId.includes('fbm')) return 'facebook-messenger';
    if (conversation.providerId.startsWith('xt-')) return 'x-twitter';
  }

  return 'unknown';
};
